import { apiGetThreads, getAIContext } from "api/ai/ai.service";
import { useState, useEffect } from "react";
import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { ArrowLeft, ArrowRight, ChatCircleDots, Clock } from "@phosphor-icons/react";
import { formatDate } from "utils";
import { truncate } from "lodash";
import { colors } from "theme/foundations/colors";
import LocalSpinner from "components/LocalSpinner";

export default function AIChatHistoryPanel({ setPanel, setThreadId, activeThreadId }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const context = getAIContext();

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiGetThreads({ questionId: context.questionId })
      .then((data) => {
        setThreads(data);
      })
      .catch((err) => {
        console.error("Failed to load chat history:", err);
        setError("Failed to load chat history");
      })
      .finally(() => setLoading(false));
  }, [context.questionId]);

  const handlePickThread = (threadId) => {
    setThreadId(threadId);
    setPanel("chat");
  };

  const handleBack = (e) => {
    e.stopPropagation();
    setPanel("chat");
  };

  return (
    <Box h="100%" display="flex" flexDirection="column">
      <HStack
        spacing={2}
        mb={3}
        px={2}
        py={2}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        cursor="pointer"
        _hover={{ borderColor: "headingLime", bg: "gray.50" }}
        transition="all 0.15s"
        onClick={handleBack}
      >
        <ArrowLeft size={16} color={colors.headingGrey} />
        <Clock size={16} color={colors.headingGrey} />
        <Text fontSize="body.sm" fontWeight="semibold" color="darkTeal" flex={1}>
          Chat History
        </Text>
        <Text fontSize="body.xs" color="gray.400">
          Back to chat
        </Text>
      </HStack>

      {loading && (
        <Box display="flex" justifyContent="center" pt={8}>
          <LocalSpinner w="60px" h="60px" />
        </Box>
      )}

      {!loading && error && (
        <Box textAlign="center" pt={8} px={4}>
          <Text fontSize="body.sm" color="red.500">{error}</Text>
        </Box>
      )}

      {!loading && !error && threads.length === 0 && (
        <Box textAlign="center" pt={8} px={4}>
          <ChatCircleDots size={40} color={colors.headingGrey} style={{ margin: "0 auto 8px" }} />
          <Text fontSize="body.sm" color="gray.500">
            No previous conversations
          </Text>
        </Box>
      )}

      {!loading && threads.length > 0 && (
        <VStack spacing={2} align="stretch" flex={1} overflowY="auto" pr={1}>
          {threads.map((thread) => {
            const firstMessage = thread.messages?.[0]?.content;
            const preview = truncate(firstMessage, { length: 120 });
            const messageCount = thread.messages?.length || 0;
            const isActive = thread.id === activeThreadId;
            return (
              <Box
                key={thread.id}
                p={3}
                bg={isActive ? "limeTint.50" : "white"}
                border="1px solid"
                borderColor={isActive ? "headingLime" : "gray.200"}
                borderRadius="lg"
                cursor="pointer"
                _hover={{ borderColor: "headingLime", bg: isActive ? "limeTint.50" : "gray.50" }}
                transition="all 0.15s"
                onClick={() => handlePickThread(thread.id)}
                position="relative"
              >
                <HStack justify="space-between" mb={1}>
                  <HStack spacing={2}>
                    <Text fontSize="body.xxs" color="gray.400" fontWeight="medium">
                      {formatDate(thread.created_date, true, false)}
                    </Text>
                    {isActive && (
                      <Box bg="headingLime" borderRadius="full" px={2} py={0}>
                        <Text fontSize="10px" color="white" fontWeight="semibold">
                          Current
                        </Text>
                      </Box>
                    )}
                  </HStack>
                  <ArrowRight size={14} color={colors.headingGrey} />
                </HStack>
                {preview && (
                  <Text fontSize="body.xs" color="darkTeal" noOfLines={2} mb={1.5}>
                    {preview}
                  </Text>
                )}
                <Text fontSize="body.xxs" color="gray.400">
                  {messageCount} message{messageCount !== 1 ? "s" : ""}
                </Text>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
